import {
  type CodeLibrariesActions,
  type CodeComponentTemplateDrag,
  type VCCStyleOverridesMap,
  type VCCTextOverridesMap,
  type VCCTextEditingTarget,
  type VCCInstantApplyLineRange,
  type PendingCreatorPrompt,
  getStyleOverrideKey,
  getSourceStyleOverrideKey,
  getSourceStyleOverrideKeyBase,
  getTextOverrideKey,
  withoutLegacyVariantlessOverride,
} from './actionCreators';
import type {VisualEditsMessageSummary} from './summarizeVisualEditsForChat';
import type {
  CodeComponentTemplateBranding,
  LocalLibraryDescriptor,
} from '@packages/systems/code-libraries/core';
import {
  reconcileAppliedStyleOverrides,
  vccStyleOverrideHasChanges,
} from './vccStyleOverrideUtils';
import {
  isSameVCCTextEditingTarget,
  pruneAppliedTextOverrides,
  vccTextOverrideHasChanges,
} from './vccTextOverrideUtils';
import {
  sourceFileKey,
  type DesignerSourceFile,
} from '@packages/domains/code-libraries/data-types';
import {
  CODE_BUILD_STARTED,
  CODE_BUILD_SUCCEEDED,
  CODE_BUILD_FAILED,
  CODE_EVALUATED,
  CODE_COMPONENT_UPDATED,
  CODE_COMPONENT_SOURCE_DRAFT_CHANGED,
  CODE_COMPONENT_SOURCE_DRAFTS_CLEARED,
  CODE_COMPONENT_SOURCE_FILE_SAVE_REFUSED,
  VCC_SOURCE_FILE_CONTENT_SAVED,
  VCC_SOURCE_FILE_DELETED,
  VCC_SOURCE_FILE_RENAMED,
  CODE_COMPONENT_EDITOR_PANE_SHOW,
  CODE_COMPONENT_EDITOR_PANE_HIDE,
  CODE_COMPONENT_EDITOR_PANE_MOVE,
  CODE_COMPONENT_EDITOR_PANE_RESIZE,
  CODE_COMPONENT_SOURCE_FILE_SELECTED,
  CODE_COMPONENT_SOURCE_TREE_COLLAPSED,
  CODE_COMPONENT_CREATED,
  CODE_COMPONENT_TEMPLATE_DRAG_STARTED,
  CODE_COMPONENT_TEMPLATE_WELCOME_SHOWN,
  CODE_COMPONENT_CREATOR_DISMISSED,
  CODE_COMPONENT_CREATOR_PROMPT_SET,
  CODE_COMPONENT_CLARIFICATION_STARTED,
  CODE_COMPONENT_CLARIFICATION_ENDED,
  VCC_STYLE_OVERRIDES_UPDATED,
  VCC_STYLE_OVERRIDES_CLEARED,
  VCC_STYLE_OVERRIDE_GROUP_REMOVED,
  VCC_STYLE_OVERRIDES_IDENTITY_DROPPED,
  VCC_STYLE_OVERRIDES_INSTANT_APPLIED,
  VCC_STYLE_OVERRIDES_INSTANT_APPLY_PENDING,
  VCC_PROP_CONNECTIONS_RESOLVED,
  VCC_SELECTED_ELEMENT_REVEALED,
  VCC_DRAFT_STYLES_APPLIED,
  VCC_TEXT_OVERRIDES_UPDATED,
  VCC_TEXT_OVERRIDES_CLEARED,
  VCC_TEXT_OVERRIDE_GROUP_REMOVED,
  VCC_TEXT_EDIT_STARTED,
  VCC_TEXT_EDIT_ENDED,
  VCC_INSTANT_TEXT_APPLIED,
  VCC_INSTANT_APPLY_REPORTED,
  VCC_INSTANT_APPLY_ROLLED_BACK,
  VCC_APPLY_CANCELLED,
  VCC_APPLY_SOURCE_SNAPSHOT_DROPPED,
  VCC_APPLY_UNDO_TURN_ATTACHED,
  VCC_APPLY_SOURCE_SNAPSHOT_TAKEN,
  VISUAL_EDITING_ONBOARDING_ADVANCED,
  VISUAL_EDITING_ONBOARDING_DISMISSED,
  VISUAL_EDITING_ONBOARDING_GENERATION_STARTED,
  VISUAL_EDITING_ONBOARDING_STARTED,
  LOCAL_DEV_SERVER_REACHABILITY_CHANGED,
  LOCAL_DEV_LIBRARY_DISCOVERED,
  LOCAL_OVERRIDE_OPT_OUT_CHANGED,
} from './actionTypes';
import {
  INITIAL_STATE_COLLECTED,
  PAGE_SWITCHED,
  RAW_BRANCHED_DATA_RECEIVED,
  RAW_SITE_DATA_RECEIVED,
  SITE_INSTALLED_RESOURCES_RELOADED,
  REMOTE_FILESYSTEM_FILES_COMMITTED,
  SYMBOL_NODE_UNFOCUSED,
  COMPONENT_NODE_UNFOCUSED_ALL,
  NAVIGATED_BACK_FROM_COMPONENT_EDIT,
  UNDO,
  DRAG_STOP,
  UNMOVABLE_NODE_DRAG_ATTEMPTED,
} from '@packages/systems/designer/actions';
import type {
  aiAssistantSendMessage,
  aiAssistantSegmentEnded,
  aiAssistantCancelGeneration,
  aiAssistantRespondToExternalTool,
  aiAssistantExternalToolDetected,
} from '@packages/systems/ai/assistant-next/frontend';
// Action-type strings are mirrored locally instead of imported as values to
// avoid loading the AssistantChat → artifact-renderer → rehype-raw chain
// via the package's public barrel, which breaks Jest in dependent packages.
// Other packages address the same chain via `transformIgnorePatterns` (see
// packages/systems/component-utils/jest.config.ts and
// packages/systems/ai/assistant-next/frontend/jest.config.ts), but those
// patterns don't match `rehype-raw` and snowball through the unified
// ecosystem. The `import type` above is erased at compile time so the chain
// stays unloaded, and each `satisfies` ties the literal to the matching
// creator's emitted `type` so a rename upstream fails the typecheck here.
const AI_ASSISTANT_SEND_MESSAGE =
  'AI_ASSISTANT_SEND_MESSAGE' satisfies ReturnType<
    typeof aiAssistantSendMessage
  >['type'];
const AI_ASSISTANT_SEGMENT_ENDED =
  'AI_ASSISTANT_SEGMENT_ENDED' satisfies ReturnType<
    typeof aiAssistantSegmentEnded
  >['type'];
const AI_ASSISTANT_CANCEL_GENERATION =
  'AI_ASSISTANT_CANCEL_GENERATION' satisfies ReturnType<
    typeof aiAssistantCancelGeneration
  >['type'];
const AI_ASSISTANT_RESPOND_TO_EXTERNAL_TOOL =
  'AI_ASSISTANT_RESPOND_TO_EXTERNAL_TOOL' satisfies ReturnType<
    typeof aiAssistantRespondToExternalTool
  >['type'];
const AI_ASSISTANT_EXTERNAL_TOOL_DETECTED =
  'AI_ASSISTANT_EXTERNAL_TOOL_DETECTED' satisfies ReturnType<
    typeof aiAssistantExternalToolDetected
  >['type'];
import type {PersistentUIState} from '@packages/shared/types/persistentUIState';

export type PanePosition = {x: number; y: number};
export type PaneSize = {width: number; height: number};
export type FloatingPaneState = {
  isVisible: boolean;
  position: PanePosition;
  size: PaneSize;
};

type InitialStateCollectedAction = {
  type: typeof INITIAL_STATE_COLLECTED;
  payload: {persistedUIState?: PersistentUIState};
};

type SourceFilesHydratedAction = {
  type:
    | typeof RAW_BRANCHED_DATA_RECEIVED
    | typeof RAW_SITE_DATA_RECEIVED
    | typeof SITE_INSTALLED_RESOURCES_RELOADED;
  siteData?: {sourceFiles?: DesignerSourceFile[]};
};

type SourceFilesCatchupAction = {
  type: 'MULTIPLAYER_DATA_CATCHUP_RESPONDED';
  payload: {response: {sourceFiles?: DesignerSourceFile[]}};
};

type PageSwitchedAction = {
  type: typeof PAGE_SWITCHED;
};

type AIAssistantSegmentEndedAction = {
  type: typeof AI_ASSISTANT_SEGMENT_ENDED;
};

type AIAssistantSendMessageAction = {
  type: typeof AI_ASSISTANT_SEND_MESSAGE;
  /** Raw client-only snapshot; intentionally excluded from the message payload. */
  vccStyleOverridesSnapshot?: VCCStyleOverridesMap;
  /** The send left every draft out of its context, so none is mid-apply. */
  vccDraftsWithheld?: boolean;
};

type AIAssistantCancelGenerationAction = {
  type: typeof AI_ASSISTANT_CANCEL_GENERATION;
};

type AIAssistantRespondToExternalToolAction = {
  type: typeof AI_ASSISTANT_RESPOND_TO_EXTERNAL_TOOL;
  payload?: {
    toolCallEvent?: {tool?: {name?: string}};
    response?: {error?: unknown; result?: unknown};
  };
};

type AIAssistantExternalToolDetectedAction = {
  type: typeof AI_ASSISTANT_EXTERNAL_TOOL_DETECTED;
  payload?: {
    toolCallEvent?: {tool?: {name?: string}};
  };
};

type SymbolNodeUnfocusedAction = {
  type: typeof SYMBOL_NODE_UNFOCUSED;
};

type ComponentNodeUnfocusedAllAction = {
  type: typeof COMPONENT_NODE_UNFOCUSED_ALL;
};

type NavigatedBackFromComponentEditAction = {
  type: typeof NAVIGATED_BACK_FROM_COMPONENT_EDIT;
};

type UndoAction = {
  type: typeof UNDO;
};

export type CodeComponentUIState = {
  /**
   * Whether `devlink dev`'s local server answered the last poll. Written by
   * `LocalDevOverrideSync`; `true` until the first poll resolves.
   */
  localDevServerReachable: boolean;
  /**
   * The library `devlink dev` is serving, or null. Written by
   * `LocalDevOverrideSync`.
   */
  localDevDiscoveredDescriptor: LocalLibraryDescriptor | null;
  /**
   * Libraries (by profile id) whose local override the user switched off, until
   * the next page load. Without it the override would be added again as soon as
   * it is removed, since a library that is installed and has none gets one.
   */
  localOverrideOptedOutProfileIds: readonly string[];
  js: string;
  css: string;
  sourceRevisions?: Readonly<Record<string, string>>;
  buildVersion: number;
  evaluatedVersion: number;
  diagnostics: string;
  /**
   * Unsaved editor content per file, keyed by `sourceFileKey` (or
   * `mainSourceDraftKey` for the component's own source). A file is dirty
   * exactly when it has an entry here, so the leave guard covers all of them.
   */
  sourceFileDrafts: Readonly<Record<string, string>>;
  /** Derived from `sourceFileDrafts` -- never assigned on its own. */
  hasUnsavedCodeChanges: boolean;
  /**
   * Bumped whenever every draft is wiped at once -- the leave guard's
   * save-or-discard, or an undo back to pristine -- never by a single file's
   * own save/rename/delete. An explicit signal for the editor to watch,
   * instead of inferring one from `sourceFileDrafts` losing an entry, which
   * also happens per-file and mid-stream.
   */
  draftsClearedVersion: number;
  /**
   * Source file keys whose save a write rule refused, oldest first. Only the
   * keys are kept -- the editor derives the message from the content on screen,
   * so a file leaves this list by saving, never by being acknowledged.
   */
  refusedSourceFileKeys: readonly string[];
  /**
   * Bumped on every refused save, including one that repeats an earlier
   * refusal. The editor watches this to open the file still waiting, and the
   * key list alone cannot say a refusal just happened.
   */
  sourceFileSaveRefusalVersion: number;
  editorPane: FloatingPaneState;
  /**
   * The source file key (`${folderId ?? ''}/${name}`) the editor is currently
   * showing, or null to show the canvas-focused component's own source (the
   * default single-file view). Set by clicking a row in the VCC file tree.
   */
  activeSourceFileKey: string | null;
  /**
   * When true, the editor's source-file tree is collapsed out of the layout and
   * the editor takes the full pane width. Kept out of `editorPane` so toggling
   * the tree doesn't change that object's identity and re-run the floating
   * pane's position/size layout effects.
   */
  isSourceTreeCollapsed: boolean;
  isCreatorDismissed: boolean;
  pendingCreatorPrompt: PendingCreatorPrompt | null;
  /** Transient creation signal; restored components show the welcome in full. */
  pendingTemplateWelcomeComponentId: string | null;
  pendingTemplateDrag: CodeComponentTemplateDrag | null;
  /** Page branding read when the creator's stub was made; its canvas has none. */
  creatorTemplateBranding: CodeComponentTemplateBranding | null;
  /**
   * When true, the fullscreen AICC clarification ViewportLayer is shown after
   * the creator is dismissed. Scaffold/sync file commits must not clear this —
   * they race ahead of ask-user-question and would dump the question into the
   * sidebar. Cleared when the user answers, a build write tool runs, the
   * create turn idles without HITL (after arming), or on create/page reset.
   */
  isAwaitingClarification?: boolean;
  /**
   * User prompt that opened the fullscreen clarification shell. Snapshotted
   * from the creator dismiss payload so it survives auto-send clearing
   * `pendingCreatorPrompt`. Also acts as a session marker so a late
   * ask-user-question can reopen the shell after an idle dismiss.
   */
  clarificationUserPrompt: string | null;
  /**
   * True once the create prompt has been sent to the agent while the
   * clarification shell is up. Ignores early SEGMENT_ENDED from
   * clearConversation/cancel; only an armed idle turn may dismiss the shell
   * without HITL or a write tool.
   */
  clarificationCreateTurnArmed: boolean;
  vccStyleOverrides: VCCStyleOverridesMap;
  /**
   * When true, the next CODE_EVALUATED reconciles the style snapshot sent to
   * AIA. Deferring this until the new code lands keeps the canvas from flashing
   * back to the un-edited baseline mid-turn.
   */
  clearVccStyleOverridesOnNextEval: boolean;
  /**
   * When true, the next AI_ASSISTANT_SEGMENT_ENDED skips arming the deferred
   * clear. Used after AI_ASSISTANT_CANCEL_GENERATION so canceled drafts stay
   * visible for the user to retry or keep editing.
   */
  skipVccStyleOverridesClearOnNextTurnEnd: boolean;
  /** Style drafts included in the current AIA message. */
  sentVccStyleOverrides: VCCStyleOverridesMap;
  /**
   * The component's source as it stood when the user clicked Apply, before either
   * deterministic engine ran (EXT-1976).
   *
   * Cancel has to put source back whoever wrote it, and the deterministic pass is
   * only one of the writers: the assistant applies drafts through
   * `edit-component-code` / `set-component-code`, which reach source through the
   * Designer Extension API rather than the file-commit path the reconciliation
   * watches. A rollback record covers only the deterministic write, and is absent
   * entirely when the engines declined every draft — the common case for a queue
   * the assistant ends up applying. This snapshot is taken on every apply, so
   * Stop has something to restore either way.
   *
   * Live exactly as long as Stop is on screen: taken at the click, dropped when
   * the turn completes — the same event that turns Stop back into Send. Nothing
   * else can write source in between, since the editor is read-only for the whole
   * turn (`isEditorReadOnly = isAgentBusy || !canEditCodeComponents`), so the
   * restore cannot clobber anything the user typed.
   */
  vccApplySourceSnapshot: {
    componentId: string;
    code: string;
    /**
     * The undo bracket this apply opened around its write, once it has one, so a
     * rollback can name the transcript entry it is reverting (EXT-2020's
     * `aiAssistantInstantApplyReverted`). Null until the write opens it, and
     * null for an apply whose engines wrote nothing — which records no entry
     * either, so there is nothing to revert.
     */
    undoTurnId: string | null;
  } | null;
  /**
   * Whether a cancel has claimed the snapshot above and its rollback is still
   * owed (EXT-1976).
   *
   * Stop both cancels the turn and ends it, and the end is what puts Send back —
   * so the turn-end below arrives for a cancelled turn too. Expiring the
   * snapshot there treated "the user stopped it" as "the assistant finished
   * answering", and the rollback, which lands from a subscriber a tick later,
   * then found nothing to restore. A claimed snapshot outlives that turn end and
   * is released by the rollback that consumes it, or by the next apply.
   */
  vccApplySourceSnapshotClaimed: boolean;
  /**
   * Drafts instant CSS apply (EXT-1219) already wrote into source. They stay live
   * until the rebuild lands, then CODE_EVALUATED drops the unchanged ones.
   */
  instantAppliedVccStyleOverrides: VCCStyleOverridesMap;
  /**
   * Keys the armed instant-apply batch (EXT-1219) expects to write. A guess, not a
   * record — it keeps the draft bar from offering a write already in flight. The
   * orchestrator owns it; the other draft resets here ignore it on purpose, since a
   * key only subtracts from `vccStyleOverrides` and the next payload replaces the set.
   */
  instantApplyPendingKeys: readonly string[];
  vccTextOverrides: VCCTextOverridesMap;
  clearVccTextOverridesOnNextEval: boolean;
  skipVccTextOverridesClearOnNextTurnEnd: boolean;
  /**
   * The latest send left the drafts out of its context, so the turn it started
   * is not applying them. Set and cleared by each send.
   */
  vccDraftsWithheldFromTurn: boolean;
  /**
   * Bumped every time the user calls off a pending draft apply (EXT-1976). The
   * rollback itself is a pair of dispatches the reducer cannot make — a source
   * save and a draft restore — so the style panel watches this counter and does
   * the work. A counter rather than a flag: two cancels in a row are two
   * distinct events, and nothing has to reset it.
   */
  vccApplyCancelRequests: number;
  sentVccTextOverrides: VCCTextOverridesMap;
  vccTextEditing: VCCTextEditingTarget | null;
  /**
   * Last instant-apply rewrite location, versioned so the code editor pane can
   * distinguish a fresh apply from a re-render of the same one. `version`
   * only ever grows; consumers track the last version they acted on.
   */
  vccInstantApplyHighlight:
    | (VCCInstantApplyLineRange & {version: number})
    | null;
  /**
   * What the last confirm-time instant apply wrote (EXT-1958), so the assistant
   * chat can list it while the rebuild it kicked off is still in flight. Both
   * apply surfaces go through the same batch, so reading this covers the style
   * panel's apply as well as the chat's own.
   *
   * Versioned like `vccInstantApplyHighlight`, and for the same reason: two
   * applies can write the same drafts, and a consumer has to be able to tell the
   * second from a re-render of the first. `summary` is null when the batch wrote
   * source but could not describe what it wrote — the apply still happened, so
   * the version still moves.
   */
  vccInstantApplyReport: {
    summary: VisualEditsMessageSummary | null;
    /**
     * The undo-turn markers bracketing this apply's write (EXT-2020). Lets the
     * chat match the rebuild it is reporting to the transcript entry the same
     * apply produced, so a settled entry from an earlier apply is not repainted
     * as still applying. Empty string when the write was not bracketed.
     */
    turnId: string;
    version: number;
    /**
     * `buildVersion` as the rebuild this apply kicked off started, or null while
     * that build has yet to start. It is what lets a consumer tell that rebuild's
     * evaluation from any other: `CODE_EVALUATED` bumps `evaluatedVersion`
     * unconditionally, so a build already in flight when the user clicked apply
     * would otherwise read as the apply having landed while the canvas still
     * shows the old component. Same two-step the AICC onboarding path uses, and
     * for the same reason — see `onboardingAiBuildStartVersion`.
     */
    buildStartVersion: number | null;
  } | null;
  /**
   * Source lines of the element currently selected on the canvas (EXT-870), so
   * the code editor can scroll to them and tint them. Versioned like
   * `vccInstantApplyHighlight` so a pane remount doesn't replay a stale reveal.
   * Kept separate from that field because a selection is not a change: it gets a
   * quiet persistent tint, not the apply flash.
   */
  vccSelectedSourceRange: (VCCInstantApplyLineRange & {version: number}) | null;
  pendingVisualEditingOnboarding: boolean;
  onboardingAiTurnComplete: boolean;
  onboardingCodeEvaluated: boolean;
  onboardingAiWritePending: boolean;
  onboardingAiBuildStartVersion: number | null;
  showVisualEditingOnboarding: boolean;
  /**
   * Which of the three on-canvas tour steps is showing. It starts at 1 when the
   * user reaches the tour straight from their first generation, and at 2 when
   * the components-button popover already showed them step 1 before sending
   * them to the component canvas. Kept here rather than in the popover so a
   * Style panel remount (a deselection, say) doesn't rewind the tour.
   */
  visualEditingOnboardingStep: 1 | 2 | 3;
  hasMadeVisualEdit: boolean;
  /**
   * Bumped whenever SourceFilesStore is replaced wholesale by a server load.
   * `mergeWholesaleSourceFiles` rebuilds from the incoming list alone, so a
   * short reload is indistinguishable from a delete; SourceFilesSyncEngine
   * watches this to tell them apart before deriving tombstones.
   */
  sourceFilesHydrationEpoch: number;

  /**
   * Bumped whenever the live component map is replaced wholesale by a server
   * load. The mirror of `sourceFilesHydrationEpoch`, for the other half of the
   * pair: a reload rebuilds the map from its payload alone while source files
   * survive untouched, so a component missing from a short or stale payload is
   * indistinguishable from one the user deleted. A shrinking map cannot tell
   * them apart -- only the action can -- so the unused-file collector watches
   * this and treats everything as never-seen after a reload (EXT-1962).
   */
  componentMapHydrationEpoch: number;
};

export const initialEditorPaneState: FloatingPaneState = {
  isVisible: true,
  position: {x: 0, y: 0},
  size: {width: 480, height: 400},
};

/**
 * Draft key for a component's own source, which has no `sourceFileKey`. Scoped
 * by component because drafts outlive leaving one: a shared key would seed the
 * next component's buffer.
 */
const MAIN_SOURCE_DRAFT_PREFIX = '__component_source__:';

export function mainSourceDraftKey(componentId: string): string {
  return `${MAIN_SOURCE_DRAFT_PREFIX}${componentId}`;
}

/** The component a main-source draft belongs to, or null for a file draft. */
export function mainSourceDraftComponentId(key: string): string | null {
  return key.startsWith(MAIN_SOURCE_DRAFT_PREFIX)
    ? key.slice(MAIN_SOURCE_DRAFT_PREFIX.length)
    : null;
}

const EMPTY_SOURCE_FILE_DRAFTS: Readonly<Record<string, string>> = {};
const EMPTY_REFUSED_SOURCE_FILE_KEYS: readonly string[] = [];

/**
 * Replaces the draft map and keeps `hasUnsavedCodeChanges` in step with it.
 * Every write to `sourceFileDrafts` goes through here so the two can't drift.
 *
 * Also drops the refusal of any file whose draft is gone, which keeps refused
 * keys a subset of drafted ones. Without that the key outlives the refusal: the
 * file saves, the note goes away with the draft, and the person's next edit to
 * the same file warns them about a save they never attempted.
 */
function withSourceFileDrafts(
  state: CodeComponentUIState,
  drafts: Readonly<Record<string, string>>
): CodeComponentUIState {
  const hasUnsavedCodeChanges = Object.keys(drafts).length > 0;
  // Collapse an emptied map back to the shared constant: the identity bailouts
  // elsewhere compare against it, and a fresh `{}` would defeat them for good.
  const sourceFileDrafts = hasUnsavedCodeChanges
    ? drafts
    : EMPTY_SOURCE_FILE_DRAFTS;
  /**
   * Pruning compares against the collapsed map, not `drafts`, so an emptied map
   * drops every key rather than reading `undefined` on every lookup.
   */
  const hasStaleRefusal = state.refusedSourceFileKeys.some(
    (key) => sourceFileDrafts[key] === undefined
  );
  /**
   * Identity is kept when nothing is pruned: these arrays are compared by
   * reference by the selectors that read them.
   */
  const refusedSourceFileKeys = hasStaleRefusal
    ? state.refusedSourceFileKeys.filter(
        (key) => sourceFileDrafts[key] !== undefined
      )
    : state.refusedSourceFileKeys;
  if (
    state.sourceFileDrafts === sourceFileDrafts &&
    state.hasUnsavedCodeChanges === hasUnsavedCodeChanges &&
    state.refusedSourceFileKeys === refusedSourceFileKeys
  ) {
    return state;
  }
  return {
    ...state,
    sourceFileDrafts,
    hasUnsavedCodeChanges,
    refusedSourceFileKeys,
  };
}

/**
 * The draft key a file takes after a rename. Keys are `${folderId ?? ''}/${name}`
 * and a folder id never contains a slash, so the prefix survives the rename.
 */
function renamedDraftKey(key: string, name: string): string {
  return `${key.slice(0, key.indexOf('/'))}/${name}`;
}

const EMPTY_VCC_STYLE_OVERRIDES: VCCStyleOverridesMap = {};
const EMPTY_VCC_TEXT_OVERRIDES: VCCTextOverridesMap = {};
const EMPTY_INSTANT_APPLY_PENDING_KEYS: readonly string[] = [];

function applyPropConnectionResolution<
  T extends {
    nodeId: string;
    hasConnectedProp?: boolean;
    propConnectionRevision?: string;
  },
>(
  overrides: Record<string, T>,
  revision: string,
  connections: Record<string, boolean>
): Record<string, T> {
  let next = overrides;
  for (const [key, entry] of Object.entries(overrides)) {
    const connected = connections[entry.nodeId];
    if (connected === undefined) continue;
    if (
      entry.hasConnectedProp === connected &&
      entry.propConnectionRevision === revision
    ) {
      continue;
    }
    if (next === overrides) next = {...overrides};
    next[key] = {
      ...entry,
      hasConnectedProp: connected,
      propConnectionRevision: revision,
    };
  }
  return next;
}

/** Write/edit tools that mean the agent skipped HITL and started building. */
const CLARIFICATION_BUILD_TOOL_IDS = new Set([
  'write-remote-file',
  'write-remote-files',
  'edit-remote-file',
  'edit-remote-file-exact',
  'test-run-function',
]);

function clearClarificationSession(
  state: CodeComponentUIState
): CodeComponentUIState {
  return {
    ...state,
    isAwaitingClarification: false,
    clarificationUserPrompt: null,
    clarificationCreateTurnArmed: false,
  };
}

export const initialCodeComponentUIState: CodeComponentUIState = {
  localDevServerReachable: true,
  localDevDiscoveredDescriptor: null,
  localOverrideOptedOutProfileIds: [],
  js: '',
  css: '',
  buildVersion: 0,
  evaluatedVersion: 0,
  diagnostics: '',
  sourceFileDrafts: EMPTY_SOURCE_FILE_DRAFTS,
  hasUnsavedCodeChanges: false,
  draftsClearedVersion: 0,
  refusedSourceFileKeys: EMPTY_REFUSED_SOURCE_FILE_KEYS,
  sourceFileSaveRefusalVersion: 0,
  editorPane: initialEditorPaneState,
  activeSourceFileKey: null,
  isSourceTreeCollapsed: false,
  isCreatorDismissed: false,
  pendingCreatorPrompt: null,
  pendingTemplateWelcomeComponentId: null,
  pendingTemplateDrag: null,
  creatorTemplateBranding: null,
  isAwaitingClarification: false,
  clarificationUserPrompt: null,
  clarificationCreateTurnArmed: false,
  vccStyleOverrides: EMPTY_VCC_STYLE_OVERRIDES,
  clearVccStyleOverridesOnNextEval: false,
  skipVccStyleOverridesClearOnNextTurnEnd: false,
  sentVccStyleOverrides: EMPTY_VCC_STYLE_OVERRIDES,
  vccApplySourceSnapshot: null,
  vccApplySourceSnapshotClaimed: false,
  instantAppliedVccStyleOverrides: EMPTY_VCC_STYLE_OVERRIDES,
  instantApplyPendingKeys: EMPTY_INSTANT_APPLY_PENDING_KEYS,
  vccTextOverrides: EMPTY_VCC_TEXT_OVERRIDES,
  sentVccTextOverrides: EMPTY_VCC_TEXT_OVERRIDES,
  clearVccTextOverridesOnNextEval: false,
  skipVccTextOverridesClearOnNextTurnEnd: false,
  vccDraftsWithheldFromTurn: false,
  vccApplyCancelRequests: 0,
  vccTextEditing: null,
  vccInstantApplyHighlight: null,
  vccInstantApplyReport: null,
  vccSelectedSourceRange: null,
  pendingVisualEditingOnboarding: false,
  onboardingAiTurnComplete: false,
  onboardingCodeEvaluated: false,
  onboardingAiWritePending: false,
  onboardingAiBuildStartVersion: null,
  showVisualEditingOnboarding: false,
  visualEditingOnboardingStep: 1,
  hasMadeVisualEdit: false,
  sourceFilesHydrationEpoch: 0,
  componentMapHydrationEpoch: 0,
};

// Reveals the popover once both latches are set; they can arrive in either order.
function revealVisualEditingOnboardingIfReady(
  state: CodeComponentUIState
): CodeComponentUIState {
  if (
    !state.pendingVisualEditingOnboarding ||
    !state.onboardingAiTurnComplete ||
    !state.onboardingCodeEvaluated
  ) {
    return state;
  }
  return {
    ...state,
    pendingVisualEditingOnboarding: false,
    onboardingAiTurnComplete: false,
    onboardingCodeEvaluated: false,
    onboardingAiWritePending: false,
    onboardingAiBuildStartVersion: null,
    showVisualEditingOnboarding: true,
  };
}

/**
 * Bring the deferred draft clears down (EXT-1976).
 *
 * `clearVccStyleOverridesOnNextEval` means "the drafts are mid-apply"; the draft
 * bar renders that as "Applying changes" and only CODE_EVALUATED lowers it. A
 * cancelled apply need never produce one, so anything that ends an apply has to
 * lower it here or the bar has no way out. Keyed off the draft maps it missed
 * exactly the cancels taken after the queue had emptied.
 *
 * The skip latches stay keyed off the drafts: they only say "don't re-arm at the
 * end of the turn being cancelled", and there is nothing to keep visible when the
 * map is already empty. They are held until the next AI_ASSISTANT_SEND_MESSAGE,
 * which lowers them.
 *
 * Every clear comes down here, including one a committed AIA write armed. That
 * was briefly the other way round, to stop the edits sitting in source *and*
 * coming back in the queue — but the answer to that is restoring the source,
 * which the cancel now does, not dropping the queue. Keeping the reconciliation
 * armed made Stop lose the queue whenever it landed after a write and keep it
 * whenever it landed before, which is the same gesture with two outcomes.
 */
const releasePendingVccClears = (
  state: CodeComponentUIState
): CodeComponentUIState => {
  const hasStyleDrafts = state.vccStyleOverrides !== EMPTY_VCC_STYLE_OVERRIDES;
  const hasTextDrafts = state.vccTextOverrides !== EMPTY_VCC_TEXT_OVERRIDES;
  const isClearArmed =
    state.clearVccStyleOverridesOnNextEval ||
    state.clearVccTextOverridesOnNextEval;
  if (!hasStyleDrafts && !hasTextDrafts && !isClearArmed) return state;
  // Every clear comes down, and the drafts stay queued. Cancel restores the
  // source the apply started from — whichever writer had changed it — so the
  // premise the reconciliation runs on ("these drafts are in source now") is
  // false by the time it would fire. Letting it prune here is what took the
  // user's queue away on a Stop that landed after the assistant had written,
  // while a Stop a moment earlier kept it: same intent, two different outcomes.
  // Reverting the code and keeping the edits queued is the one the user can
  // carry on from.
  return {
    ...state,
    clearVccStyleOverridesOnNextEval: false,
    // Raised, never assigned: a second release event (a stream soft-end landing
    // just after a user cancel, or any release taken while the map happens to be
    // empty) must not lower a latch that is still owed. Only
    // AI_ASSISTANT_SEND_MESSAGE lowers them.
    skipVccStyleOverridesClearOnNextTurnEnd:
      state.skipVccStyleOverridesClearOnNextTurnEnd || hasStyleDrafts,
    clearVccTextOverridesOnNextEval: false,
    skipVccTextOverridesClearOnNextTurnEnd:
      state.skipVccTextOverridesClearOnNextTurnEnd || hasTextDrafts,
  };
};

export const codeComponentUIReducer = (
  state: CodeComponentUIState = initialCodeComponentUIState,
  action:
    | CodeLibrariesActions
    | {type: typeof DRAG_STOP | typeof UNMOVABLE_NODE_DRAG_ATTEMPTED}
    | InitialStateCollectedAction
    | PageSwitchedAction
    | SourceFilesHydratedAction
    | SourceFilesCatchupAction
    | AIAssistantCancelGenerationAction
    | AIAssistantSegmentEndedAction
    | AIAssistantSendMessageAction
    | AIAssistantRespondToExternalToolAction
    | AIAssistantExternalToolDetectedAction
    | SymbolNodeUnfocusedAction
    | ComponentNodeUnfocusedAllAction
    | NavigatedBackFromComponentEditAction
    | UndoAction,
  // True only on the final segment of the turn (no external tool still pending),
  // not the intermediate segment-ends a multi-tool turn emits.
  isAiTurnComplete: boolean = false,
  // True when this UNDO reverted the canvas component back to its initial
  // source (see didUndoRevertCanvasSourceToPristine in CodeLibrariesSlice).
  didUndoRevertSourceToPristine: boolean = false,
  // The tool still pending when an external file commit is reduced. This
  // distinguishes AIA writes from user syncs and other commit sources.
  pendingAiExternalToolName?: string
): CodeComponentUIState => {
  switch (action.type) {
    case INITIAL_STATE_COLLECTED: {
      const persisted = action.payload.persistedUIState?.codeEditorPaneSettings;
      // Only visibility is restored. Position and size stay at their defaults
      // so the pane keeps opening next to its canvas-bar toggle instead of at
      // coordinates saved under a different viewport or sidebar layout.
      //
      // This action lands mid-boot, so stay a strict no-op unless the stored
      // visibility actually differs: an extra store update here re-orders the
      // canvas code-build handshake and can strand code components on
      // "Loading code component".
      if (!persisted || persisted.isVisible === state.editorPane.isVisible) {
        return state;
      }
      return {
        ...state,
        editorPane: {...state.editorPane, isVisible: persisted.isVisible},
      };
    }
    case CODE_BUILD_SUCCEEDED: {
      return {
        ...state,
        js: action.payload.js,
        css: action.payload.css,
        sourceRevisions: action.payload.sourceRevisions,
        buildVersion: state ? state.buildVersion + 1 : 0,
        evaluatedVersion: state ? state.evaluatedVersion : 0,
        diagnostics: '',
      };
    }
    case CODE_BUILD_FAILED: {
      if (state === null) {
        return state;
      }
      return {
        ...state,
        js: '',
        css: '',
        buildVersion: state ? state.buildVersion + 1 : 0,
        diagnostics: action.payload,
      };
    }
    case CODE_BUILD_STARTED: {
      // The first build to start after an instant apply is the rebuild that
      // picks up what it wrote. Same snapshot, same reason as the AIA write
      // below; taken here rather than on the apply itself because a build
      // already in flight at that point can still complete and bump
      // buildVersion without containing the applied edits.
      const report =
        state.vccInstantApplyReport &&
        state.vccInstantApplyReport.buildStartVersion === null
          ? {
              ...state.vccInstantApplyReport,
              buildStartVersion: state.buildVersion,
            }
          : state.vccInstantApplyReport;

      // The first build to start after the AIA write is the rebuild that picks
      // up the generated code — a blank build already in flight started before
      // the write, so it doesn't trigger this. Snapshot the completed-build
      // count now; the CODE_EVALUATED whose buildVersion exceeds it is that
      // rebuild's evaluation.
      if (!state.onboardingAiWritePending) {
        return report === state.vccInstantApplyReport
          ? state
          : {...state, vccInstantApplyReport: report};
      }
      return {
        ...state,
        vccInstantApplyReport: report,
        onboardingAiWritePending: false,
        onboardingAiBuildStartVersion: state.buildVersion,
      };
    }
    case CODE_EVALUATED: {
      const hasSentStyleOverrides =
        state.clearVccStyleOverridesOnNextEval &&
        Object.keys(state.sentVccStyleOverrides).length > 0;
      const hasInstantAppliedStyleOverrides =
        Object.keys(state.instantAppliedVccStyleOverrides).length > 0;
      const appliedStyleSnapshots = [
        ...(hasSentStyleOverrides ? [state.sentVccStyleOverrides] : []),
        ...(hasInstantAppliedStyleOverrides
          ? [state.instantAppliedVccStyleOverrides]
          : []),
      ];
      const remainingStyle =
        appliedStyleSnapshots.length > 0
          ? reconcileAppliedStyleOverrides(
              state.vccStyleOverrides,
              appliedStyleSnapshots
            )
          : state.vccStyleOverrides;
      const remainingText = state.clearVccTextOverridesOnNextEval
        ? pruneAppliedTextOverrides(
            state.vccTextOverrides,
            state.sentVccTextOverrides
          )
        : state.vccTextOverrides;

      let next: CodeComponentUIState = {
        ...state,
        evaluatedVersion: state ? state.evaluatedVersion + 1 : 0,
        clearVccStyleOverridesOnNextEval: false,
        vccStyleOverrides:
          appliedStyleSnapshots.length > 0 &&
          Object.keys(remainingStyle).length === 0
            ? EMPTY_VCC_STYLE_OVERRIDES
            : remainingStyle,
        sentVccStyleOverrides: state.clearVccStyleOverridesOnNextEval
          ? EMPTY_VCC_STYLE_OVERRIDES
          : state.sentVccStyleOverrides,
        instantAppliedVccStyleOverrides: hasInstantAppliedStyleOverrides
          ? EMPTY_VCC_STYLE_OVERRIDES
          : state.instantAppliedVccStyleOverrides,
        clearVccTextOverridesOnNextEval: false,
        vccTextOverrides:
          state.clearVccTextOverridesOnNextEval &&
          Object.keys(remainingText).length === 0
            ? EMPTY_VCC_TEXT_OVERRIDES
            : remainingText,
        sentVccTextOverrides: state.clearVccTextOverridesOnNextEval
          ? EMPTY_VCC_TEXT_OVERRIDES
          : state.sentVccTextOverrides,
      };

      if (
        state.pendingVisualEditingOnboarding &&
        state.onboardingAiBuildStartVersion !== null &&
        state.buildVersion > state.onboardingAiBuildStartVersion &&
        !state.onboardingCodeEvaluated
      ) {
        next = {...next, onboardingCodeEvaluated: true};
      }
      return revealVisualEditingOnboardingIfReady(next);
    }
    case CODE_COMPONENT_SOURCE_DRAFT_CHANGED: {
      const {key, content} = action.payload;
      if (content === null) {
        if (state.sourceFileDrafts[key] === undefined) return state;
        const {[key]: _dropped, ...rest} = state.sourceFileDrafts;
        return withSourceFileDrafts(state, rest);
      }
      if (state.sourceFileDrafts[key] === content) return state;
      return withSourceFileDrafts(state, {
        ...state.sourceFileDrafts,
        [key]: content,
      });
    }
    case CODE_COMPONENT_SOURCE_FILE_SAVE_REFUSED: {
      /**
       * Union rather than replace: the leave guard refuses every draft it could
       * not write at once, and a later Save-button refusal must not drop the
       * others. The version bump is what marks the event -- see the payload's
       * doc comment.
       */
      return {
        ...state,
        refusedSourceFileKeys: [
          ...new Set([...state.refusedSourceFileKeys, ...action.payload.keys]),
        ],
        sourceFileSaveRefusalVersion: state.sourceFileSaveRefusalVersion + 1,
      };
    }
    case CODE_COMPONENT_SOURCE_DRAFTS_CLEARED: {
      // Only bump the version when there was something to discard, so a
      // no-op dispatch (nothing drafted) stays a no-op.
      if (Object.keys(state.sourceFileDrafts).length === 0) return state;
      return {
        ...withSourceFileDrafts(
          {...state, draftsClearedVersion: state.draftsClearedVersion + 1},
          EMPTY_SOURCE_FILE_DRAFTS
        ),
        refusedSourceFileKeys: EMPTY_REFUSED_SOURCE_FILE_KEYS,
      };
    }
    // A draft outlives the editor, so it has to be dropped wherever its basis
    // goes -- and in the reducer, not an editor effect, which only ever sees
    // the file on screen. Off-screen is the case that bites: edit the component
    // source, open a sub-file, let the agent rewrite the component, and the
    // stale main draft would otherwise overwrite its work on the next save.
    // Collaborator writes arrive through the multiplayer sync path, which these
    // cases do not see; that residue is EXT-1967.
    case CODE_COMPONENT_UPDATED: {
      if (action.payload.metadataEdit) return state;
      const key = mainSourceDraftKey(action.payload.componentId);
      const {[key]: spent, ...rest} = state.sourceFileDrafts;
      if (spent === undefined) return state;
      return withSourceFileDrafts(state, rest);
    }
    case VCC_SOURCE_FILE_CONTENT_SAVED: {
      const key = sourceFileKey(action.payload);
      const {[key]: spent, ...rest} = state.sourceFileDrafts;
      if (spent === undefined) return state;
      return withSourceFileDrafts(state, rest);
    }
    // Left under a dead key, a draft keeps the leave guard firing for a row
    // nothing shows any more, and comes back on the next file to take the name.
    case VCC_SOURCE_FILE_DELETED: {
      const {[action.payload.key]: dropped, ...rest} = state.sourceFileDrafts;
      if (dropped === undefined) return state;
      return withSourceFileDrafts(state, rest);
    }
    case VCC_SOURCE_FILE_RENAMED: {
      const {key, name} = action.payload;
      const {[key]: moved, ...rest} = state.sourceFileDrafts;
      if (moved === undefined) return state;
      return withSourceFileDrafts(state, {
        ...rest,
        [renamedDraftKey(key, name)]: moved,
      });
    }
    case CODE_COMPONENT_EDITOR_PANE_SHOW: {
      if (state.editorPane.isVisible) return state;
      return {...state, editorPane: {...state.editorPane, isVisible: true}};
    }
    case CODE_COMPONENT_EDITOR_PANE_HIDE: {
      if (!state.editorPane.isVisible) return state;
      return {...state, editorPane: {...state.editorPane, isVisible: false}};
    }
    case CODE_COMPONENT_EDITOR_PANE_MOVE: {
      return {
        ...state,
        editorPane: {...state.editorPane, position: action.payload},
      };
    }
    case CODE_COMPONENT_EDITOR_PANE_RESIZE: {
      return {
        ...state,
        editorPane: {...state.editorPane, size: action.payload},
      };
    }
    case CODE_COMPONENT_SOURCE_FILE_SELECTED: {
      if (state.activeSourceFileKey === action.payload) return state;
      return {...state, activeSourceFileKey: action.payload};
    }
    case RAW_SITE_DATA_RECEIVED:
    case SITE_INSTALLED_RESOURCES_RELOADED: {
      // Bumped independently: a payload can rebuild the component map with no
      // `sourceFiles` key, and a shared guard would skip that re-baseline too.
      const hasSourceFiles = Boolean(action.siteData?.sourceFiles);
      return {
        ...state,
        sourceFilesHydrationEpoch:
          state.sourceFilesHydrationEpoch + (hasSourceFiles ? 1 : 0),
        componentMapHydrationEpoch: state.componentMapHydrationEpoch + 1,
      };
    }
    // Despite the name this is any wholesale reload, not just page branching
    // (see the TODO on `reloadSite`) -- plugin and library install/update go
    // through it. It rebuilds the component map and leaves source files alone.
    case RAW_BRANCHED_DATA_RECEIVED: {
      return {
        ...state,
        componentMapHydrationEpoch: state.componentMapHydrationEpoch + 1,
      };
    }
    case 'MULTIPLAYER_DATA_CATCHUP_RESPONDED': {
      // Same reasoning as RAW_SITE_DATA_RECEIVED: catchup always applies the
      // component map, independent of whether sourceFiles is present.
      const hasSourceFiles = Boolean(action.payload.response.sourceFiles);
      return {
        ...state,
        sourceFilesHydrationEpoch:
          state.sourceFilesHydrationEpoch + (hasSourceFiles ? 1 : 0),
        componentMapHydrationEpoch: state.componentMapHydrationEpoch + 1,
      };
    }
    case CODE_COMPONENT_SOURCE_TREE_COLLAPSED: {
      if (state.isSourceTreeCollapsed === action.payload) return state;
      return {...state, isSourceTreeCollapsed: action.payload};
    }
    case PAGE_SWITCHED: {
      if (
        !state.pendingTemplateDrag &&
        !state.isCreatorDismissed &&
        !state.pendingCreatorPrompt &&
        !state.isAwaitingClarification &&
        !state.clarificationUserPrompt &&
        !state.clarificationCreateTurnArmed &&
        state.vccStyleOverrides === EMPTY_VCC_STYLE_OVERRIDES &&
        !state.clearVccStyleOverridesOnNextEval &&
        !state.skipVccStyleOverridesClearOnNextTurnEnd &&
        state.sentVccStyleOverrides === EMPTY_VCC_STYLE_OVERRIDES &&
        state.vccTextOverrides === EMPTY_VCC_TEXT_OVERRIDES &&
        state.vccTextEditing === null &&
        state.activeSourceFileKey === null
      ) {
        return state;
      }
      // Text deferred-eval flags are preserved (see VCC_STYLE_OVERRIDES_CLEARED).
      return {
        ...state,
        pendingTemplateDrag: null,
        isCreatorDismissed: false,
        pendingCreatorPrompt: null,
        isAwaitingClarification: false,
        clarificationUserPrompt: null,
        clarificationCreateTurnArmed: false,
        vccStyleOverrides: EMPTY_VCC_STYLE_OVERRIDES,
        clearVccStyleOverridesOnNextEval: false,
        skipVccStyleOverridesClearOnNextTurnEnd: false,
        sentVccStyleOverrides: EMPTY_VCC_STYLE_OVERRIDES,
        instantAppliedVccStyleOverrides: EMPTY_VCC_STYLE_OVERRIDES,
        vccTextOverrides: EMPTY_VCC_TEXT_OVERRIDES,
        vccTextEditing: null,
        activeSourceFileKey: null,
        // Drafts survive: the editor unmounts in preview mode, where the leave
        // guard never gets to ask, so clearing would destroy work nobody was
        // offered the chance to save. Scoped keys keep them off the next one.
      };
    }
    case UNDO: {
      // Only an undo that reverted the component to its untouched initial
      // source — the DesignerReducer computes that transition. No reference
      // guard beyond this: when it fires, the component map changed anyway.
      if (!didUndoRevertSourceToPristine) {
        return state;
      }
      // Reopen the creator splash. Drafts, highlights, and the active file
      // referenced the undone code, so they reset with it — leftover drafts
      // would ride along as stale styleChanges/textChanges on the next
      // create send (useSendUserInput reads them from this store).
      // Text deferred-eval flags are preserved (see VCC_STYLE_OVERRIDES_CLEARED).
      return {
        ...clearClarificationSession(state),
        isCreatorDismissed: false,
        pendingCreatorPrompt: null,
        pendingVisualEditingOnboarding: false,
        onboardingAiTurnComplete: false,
        onboardingCodeEvaluated: false,
        onboardingAiWritePending: false,
        onboardingAiBuildStartVersion: null,
        showVisualEditingOnboarding: false,
        vccStyleOverrides: EMPTY_VCC_STYLE_OVERRIDES,
        clearVccStyleOverridesOnNextEval: false,
        skipVccStyleOverridesClearOnNextTurnEnd: false,
        sentVccStyleOverrides: EMPTY_VCC_STYLE_OVERRIDES,
        instantAppliedVccStyleOverrides: EMPTY_VCC_STYLE_OVERRIDES,
        vccTextOverrides: EMPTY_VCC_TEXT_OVERRIDES,
        vccTextEditing: null,
        vccInstantApplyHighlight: null,
        vccInstantApplyReport: null,
        vccSelectedSourceRange: null,
        activeSourceFileKey: null,
        sourceFileDrafts: EMPTY_SOURCE_FILE_DRAFTS,
        refusedSourceFileKeys: EMPTY_REFUSED_SOURCE_FILE_KEYS,
        hasUnsavedCodeChanges: false,
        // This is a draft-clearing path too -- see draftsClearedVersion's
        // doc comment. Bumped the same way CODE_COMPONENT_SOURCE_DRAFTS_CLEARED
        // is: only when there was something to clear.
        draftsClearedVersion:
          Object.keys(state.sourceFileDrafts).length > 0
            ? state.draftsClearedVersion + 1
            : state.draftsClearedVersion,
      };
    }
    case CODE_COMPONENT_TEMPLATE_DRAG_STARTED:
      return {...state, pendingTemplateDrag: action.payload};
    case DRAG_STOP:
    case UNMOVABLE_NODE_DRAG_ATTEMPTED:
      return state.pendingTemplateDrag
        ? {...state, pendingTemplateDrag: null}
        : state;
    case CODE_COMPONENT_CREATED: {
      if (action.payload.destination === 'page') {
        return {...state, pendingTemplateDrag: null};
      }
      // Reset latches in case a prior creation left them set.
      return {
        ...clearClarificationSession(state),
        pendingTemplateWelcomeComponentId: action.payload.templateId
          ? action.payload.componentId
          : null,
        editorPane: action.payload.templateId
          ? {...state.editorPane, isVisible: false}
          : state.editorPane,
        creatorTemplateBranding: action.payload.templateBranding ?? null,
        isCreatorDismissed: false,
        pendingCreatorPrompt: null,
        pendingVisualEditingOnboarding: false,
        onboardingAiTurnComplete: false,
        onboardingCodeEvaluated: false,
        onboardingAiWritePending: false,
        onboardingAiBuildStartVersion: null,
      };
    }
    case CODE_COMPONENT_TEMPLATE_WELCOME_SHOWN: {
      return state.pendingTemplateWelcomeComponentId ===
        action.payload.componentId
        ? {...state, pendingTemplateWelcomeComponentId: null}
        : state;
    }
    case VISUAL_EDITING_ONBOARDING_GENERATION_STARTED: {
      // Preserve clarificationUserPrompt — first-time create can arm onboarding
      // and the clarification shell together (PromptBox needs the snapshot).
      return {
        ...state,
        pendingVisualEditingOnboarding: true,
        onboardingAiTurnComplete: false,
        onboardingCodeEvaluated: false,
        onboardingAiWritePending: false,
        onboardingAiBuildStartVersion: null,
        visualEditingOnboardingStep: 1,
      };
    }
    case VISUAL_EDITING_ONBOARDING_STARTED: {
      return {
        ...state,
        pendingVisualEditingOnboarding: false,
        onboardingAiTurnComplete: false,
        onboardingCodeEvaluated: false,
        onboardingAiWritePending: false,
        onboardingAiBuildStartVersion: null,
        showVisualEditingOnboarding: true,
        visualEditingOnboardingStep: 2,
      };
    }
    case VISUAL_EDITING_ONBOARDING_ADVANCED: {
      if (
        !state.showVisualEditingOnboarding ||
        state.visualEditingOnboardingStep === 3
      ) {
        return state;
      }
      return {
        ...state,
        visualEditingOnboardingStep:
          state.visualEditingOnboardingStep === 1 ? 2 : 3,
      };
    }
    case VISUAL_EDITING_ONBOARDING_DISMISSED: {
      if (
        !state.showVisualEditingOnboarding &&
        !state.pendingVisualEditingOnboarding
      ) {
        return state;
      }
      return {
        ...state,
        showVisualEditingOnboarding: false,
        pendingVisualEditingOnboarding: false,
        onboardingAiTurnComplete: false,
        onboardingCodeEvaluated: false,
        onboardingAiWritePending: false,
        onboardingAiBuildStartVersion: null,
        visualEditingOnboardingStep: 1,
      };
    }
    case CODE_COMPONENT_CREATOR_DISMISSED: {
      return {
        ...state,
        isCreatorDismissed: true,
        pendingCreatorPrompt: action.payload,
        // Freeze the trigger prompt for the fullscreen shell; auto-send clears
        // pendingCreatorPrompt as soon as the message is queued.
        ...(state.isAwaitingClarification
          ? {clarificationUserPrompt: action.payload.text}
          : {}),
      };
    }
    case CODE_COMPONENT_CLARIFICATION_STARTED: {
      if (state.isAwaitingClarification) return state;
      return {
        ...state,
        isAwaitingClarification: true,
        clarificationCreateTurnArmed: false,
      };
    }
    case CODE_COMPONENT_CLARIFICATION_ENDED: {
      if (
        !state.isAwaitingClarification &&
        !state.clarificationUserPrompt &&
        !state.clarificationCreateTurnArmed
      ) {
        return state;
      }
      // Drop the fullscreen shell immediately after the user answers so the
      // AIA sidebar can show generation progress.
      return clearClarificationSession(state);
    }
    case REMOTE_FILESYSTEM_FILES_COMMITTED: {
      // A committed AIA write is the first reliable signal that the sent style
      // snapshot is on its way into source. Arm reconciliation here so a
      // conversational or errored turn cannot leave that snapshot stale.
      const shouldArmStyleReconciliation =
        state.sentVccStyleOverrides !== EMPTY_VCC_STYLE_OVERRIDES &&
        !state.clearVccStyleOverridesOnNextEval &&
        Boolean(
          pendingAiExternalToolName &&
          CLARIFICATION_BUILD_TOOL_IDS.has(pendingAiExternalToolName)
        );
      // Do not end the clarification shell on file commits. Scaffold/sync
      // commits often land before ask-user-question is pending; clearing here
      // races the question into the sidebar. The shell ends on user answer
      // (RESPOND), build write-tool detection, armed idle turn-complete, or
      // create/page reset.
      const nextState = shouldArmStyleReconciliation
        ? {...state, clearVccStyleOverridesOnNextEval: true}
        : state;

      // The AIA write landed. Don't snapshot buildVersion here: an initial
      // blank build already in flight can still complete and bump buildVersion
      // without containing the generated code. Instead, mark the write pending
      // and latch onto the next build to *start* (see CODE_BUILD_STARTED).
      if (
        !nextState.pendingVisualEditingOnboarding ||
        !nextState.isCreatorDismissed ||
        nextState.onboardingAiWritePending ||
        nextState.onboardingAiBuildStartVersion !== null
      ) {
        return nextState === state ? state : nextState;
      }
      return {...nextState, onboardingAiWritePending: true};
    }
    case CODE_COMPONENT_CREATOR_PROMPT_SET: {
      return {...state, pendingCreatorPrompt: action.payload};
    }
    case VCC_PROP_CONNECTIONS_RESOLVED: {
      const {revision, connections} = action.payload;
      const vccStyleOverrides = applyPropConnectionResolution(
        state.vccStyleOverrides,
        revision,
        connections
      );
      const vccTextOverrides = applyPropConnectionResolution(
        state.vccTextOverrides,
        revision,
        connections
      );
      return vccStyleOverrides === state.vccStyleOverrides &&
        vccTextOverrides === state.vccTextOverrides
        ? state
        : {...state, vccStyleOverrides, vccTextOverrides};
    }
    case VCC_STYLE_OVERRIDES_UPDATED: {
      const {
        nodeId,
        nativeIdPath,
        tagName,
        textContent,
        original,
        modified,
        variant,
        componentState,
        breakpointId,
        pseudoState,
        sourceIdentity,
      } = action.payload;
      // A stamped element keys by source identity instead of nodeId + scenario.
      const key = sourceIdentity
        ? getSourceStyleOverrideKey(
            sourceIdentity,
            variant?.id,
            breakpointId,
            pseudoState
          )
        : getStyleOverrideKey(
            nodeId,
            variant?.id,
            breakpointId,
            pseudoState,
            componentState?.id
          );
      const keyBase = sourceIdentity
        ? getSourceStyleOverrideKeyBase(sourceIdentity)
        : nodeId;

      let baseOverrides = state.vccStyleOverrides;
      if (variant?.id) {
        baseOverrides = withoutLegacyVariantlessOverride(
          baseOverrides,
          keyBase,
          breakpointId,
          pseudoState,
          key,
          sourceIdentity ? undefined : componentState?.id
        );
      }

      const sentEntry = state.sentVccStyleOverrides[key];
      const effectiveOriginal =
        sentEntry && !vccStyleOverrideHasChanges(original, modified)
          ? sentEntry.modified
          : original;

      // When `modified` no longer differs from `original` (the user reset the
      // last modified property back to its baseline value), drop the entry
      // entirely instead of storing an empty/no-op override. Otherwise the
      // AI Assistant still shows this element under "Style changes" with no
      // properties listed (APAI-3313). During Apply, the sent value is the
      // upcoming baseline, so reverting to the old baseline remains a draft.
      if (!vccStyleOverrideHasChanges(effectiveOriginal, modified)) {
        if (!(key in baseOverrides)) return state;
        const {[key]: _removed, ...rest} = baseOverrides;
        return {...state, vccStyleOverrides: rest};
      }

      const previous = baseOverrides[key];

      return {
        ...state,
        vccStyleOverrides: {
          ...baseOverrides,
          [key]: {
            nodeId,
            nativeIdPath,
            tagName,
            textContent,
            original: effectiveOriginal,
            modified,
            variant,
            breakpointId,
            pseudoState: pseudoState ?? null,
            ...(componentState ? {componentState} : {}),
            ...(sourceIdentity ? {sourceIdentity} : {}),
            ...(previous?.hasConnectedProp !== undefined
              ? {hasConnectedProp: previous.hasConnectedProp}
              : {}),
            ...(previous?.propConnectionRevision
              ? {propConnectionRevision: previous.propConnectionRevision}
              : {}),
          },
        },
      };
    }
    case VCC_STYLE_OVERRIDE_GROUP_REMOVED: {
      const {nodeId, variantId} = action.payload;
      const nextOverrides: VCCStyleOverridesMap = {};
      let removedAny = false;
      for (const [key, entry] of Object.entries(state.vccStyleOverrides)) {
        if (
          entry.nodeId === nodeId &&
          (entry.variant?.id ?? undefined) === variantId
        ) {
          removedAny = true;
          continue;
        }
        nextOverrides[key] = entry;
      }
      if (!removedAny) return state;
      return {...state, vccStyleOverrides: nextOverrides};
    }
    case VCC_STYLE_OVERRIDES_IDENTITY_DROPPED: {
      // Only entries that still carry a sourceIdentity are dropped.
      const droppedKeys = new Set(action.payload);
      const nextOverrides: VCCStyleOverridesMap = {};
      let removedAny = false;
      for (const [key, entry] of Object.entries(state.vccStyleOverrides)) {
        if (droppedKeys.has(key) && entry.sourceIdentity) {
          removedAny = true;
          continue;
        }
        nextOverrides[key] = entry;
      }
      if (!removedAny) return state;
      return {...state, vccStyleOverrides: nextOverrides};
    }
    case VCC_STYLE_OVERRIDES_INSTANT_APPLIED: {
      const applied = action.payload;
      if (Object.keys(applied).length === 0) return state;
      // Merged, not replaced: a second apply can land while the first rebuild runs.
      return {
        ...state,
        instantAppliedVccStyleOverrides: {
          ...state.instantAppliedVccStyleOverrides,
          ...applied,
        },
      };
    }
    case VCC_STYLE_OVERRIDES_INSTANT_APPLY_PENDING: {
      const keys = action.payload;
      // Replaced, not merged: each payload is the full, freshly computed prediction.
      const current = state.instantApplyPendingKeys;
      if (
        keys.length === current.length &&
        keys.every((key, index) => current[index] === key)
      ) {
        // The same prediction arrives many times per edit burst; don't re-render.
        return state;
      }
      return {
        ...state,
        instantApplyPendingKeys:
          keys.length === 0 ? EMPTY_INSTANT_APPLY_PENDING_KEYS : keys,
      };
    }
    case VCC_DRAFT_STYLES_APPLIED: {
      if (state.hasMadeVisualEdit) return state;
      return {...state, hasMadeVisualEdit: true};
    }
    case AI_ASSISTANT_SEGMENT_ENDED: {
      // Defer clearing until CODE_EVALUATED so drafts stay visible while AIA
      // applies changes and can be retried if the turn is cancelled.
      let next = state;
      // The apply's source snapshot lives exactly as long as Stop does: once the
      // assistant has finished answering, its writes are the user's to keep or
      // undo through the normal Undo, and a snapshot held past here would let an
      // unrelated later Stop revert a finished apply.
      //
      // Unless a cancel claimed it. This same turn end arrives when the user
      // presses Stop — that is what puts Send back — and there it does not mean
      // the assistant finished, it means the user stopped it. The rollback for
      // that cancel lands from a subscriber a tick later, so expiring here left
      // it nothing to restore.
      if (
        isAiTurnComplete &&
        next.vccApplySourceSnapshot !== null &&
        !next.vccApplySourceSnapshotClaimed
      ) {
        next = {...next, vccApplySourceSnapshot: null};
      }
      // Dismiss the shell when the *create* turn idles without HITL (clear
      // prompt → generate). Ignore early SEGMENT_ENDED from clearConversation
      // until clarificationCreateTurnArmed is set by SEND_MESSAGE. Keep
      // clarificationUserPrompt so a late ask-user-question can reopen.
      if (
        state.isAwaitingClarification &&
        state.clarificationCreateTurnArmed &&
        isAiTurnComplete
      ) {
        next = {
          ...next,
          isAwaitingClarification: false,
          clarificationCreateTurnArmed: false,
        };
      }
      // Latch the completed creation turn; reveal is deferred to the helper.
      if (
        state.pendingVisualEditingOnboarding &&
        state.isCreatorDismissed &&
        isAiTurnComplete &&
        !state.onboardingAiTurnComplete
      ) {
        next = {...next, onboardingAiTurnComplete: true};
      }
      if (
        isAiTurnComplete &&
        state.skipVccStyleOverridesClearOnNextTurnEnd &&
        state.vccStyleOverrides !== EMPTY_VCC_STYLE_OVERRIDES
      ) {
        next = {...next, skipVccStyleOverridesClearOnNextTurnEnd: false};
      } else if (
        isAiTurnComplete &&
        state.sentVccStyleOverrides !== EMPTY_VCC_STYLE_OVERRIDES &&
        !state.clearVccStyleOverridesOnNextEval
      ) {
        // No source write committed during this turn, so the sent value never
        // became an upcoming baseline. Keep the drafts and drop the snapshot.
        next = {
          ...next,
          sentVccStyleOverrides: EMPTY_VCC_STYLE_OVERRIDES,
        };
      }
      if (
        state.vccTextOverrides !== EMPTY_VCC_TEXT_OVERRIDES &&
        !state.skipVccTextOverridesClearOnNextTurnEnd &&
        !state.clearVccTextOverridesOnNextEval
      ) {
        next = {...next, clearVccTextOverridesOnNextEval: true};
      }
      if (next.vccTextEditing !== null) {
        next = {...next, vccTextEditing: null};
      }
      return revealVisualEditingOnboardingIfReady(next);
    }
    case AI_ASSISTANT_EXTERNAL_TOOL_DETECTED: {
      const toolName = action.payload?.toolCallEvent?.tool?.name;
      // Late ask-user-question after awaiting was cleared: reopen the
      // fullscreen shell using the snapshotted create prompt as the session
      // marker (instead of dumping the card into the sidebar).
      if (
        toolName === 'ask-user-question' &&
        state.clarificationUserPrompt &&
        !state.isAwaitingClarification
      ) {
        return {
          ...state,
          isAwaitingClarification: true,
          clarificationCreateTurnArmed: true,
        };
      }
      // Agent started building without HITL — end the create-clarification session.
      if (
        state.clarificationUserPrompt &&
        toolName &&
        CLARIFICATION_BUILD_TOOL_IDS.has(toolName)
      ) {
        return clearClarificationSession(state);
      }
      return state;
    }
    case AI_ASSISTANT_RESPOND_TO_EXTERNAL_TOOL: {
      // Cancel or success ends the create-clarification session so the user
      // lands in the AIA sidebar as soon as they answer.
      if (
        !state.isAwaitingClarification ||
        action.payload?.toolCallEvent?.tool?.name !== 'ask-user-question'
      ) {
        return state;
      }
      return clearClarificationSession(state);
    }
    case AI_ASSISTANT_SEND_MESSAGE: {
      // Arm the create turn so a later idle SEGMENT_ENDED can dismiss the
      // shell when the agent skips ask-user-question and builds (or finishes
      // without HITL). Also end a leftover session if the user sends from
      // the sidebar after the shell already dropped.
      const shouldArmClarificationTurn = Boolean(state.isAwaitingClarification);
      const shouldEndClarificationSession =
        Boolean(state.clarificationUserPrompt) &&
        !state.isAwaitingClarification;
      const sentVccStyleOverrides =
        action.vccStyleOverridesSnapshot &&
        Object.keys(action.vccStyleOverridesSnapshot).length > 0
          ? action.vccStyleOverridesSnapshot
          : EMPTY_VCC_STYLE_OVERRIDES;
      // A send that withheld the drafts must not mark them sent or let the
      // turn end arm their clear: they were never handed to the assistant.
      const draftsWithheld = Boolean(action.vccDraftsWithheld);
      const sentVccTextOverrides = draftsWithheld
        ? EMPTY_VCC_TEXT_OVERRIDES
        : state.vccTextOverrides;
      const snapshotsUnchanged =
        state.sentVccStyleOverrides === sentVccStyleOverrides &&
        state.sentVccTextOverrides === sentVccTextOverrides;
      if (
        !state.skipVccStyleOverridesClearOnNextTurnEnd &&
        !state.clearVccStyleOverridesOnNextEval &&
        state.skipVccTextOverridesClearOnNextTurnEnd === draftsWithheld &&
        state.vccDraftsWithheldFromTurn === draftsWithheld &&
        !state.clearVccTextOverridesOnNextEval &&
        snapshotsUnchanged &&
        !shouldArmClarificationTurn &&
        !shouldEndClarificationSession
      ) {
        return state;
      }
      return {
        ...state,
        ...(shouldArmClarificationTurn
          ? {clarificationCreateTurnArmed: true}
          : {}),
        ...(shouldEndClarificationSession
          ? {
              clarificationUserPrompt: null,
              clarificationCreateTurnArmed: false,
            }
          : {}),
        skipVccStyleOverridesClearOnNextTurnEnd: false,
        clearVccStyleOverridesOnNextEval: false,
        sentVccStyleOverrides,
        skipVccTextOverridesClearOnNextTurnEnd: draftsWithheld,
        vccDraftsWithheldFromTurn: draftsWithheld,
        clearVccTextOverridesOnNextEval: false,
        sentVccTextOverrides,
      };
    }
    case AI_ASSISTANT_CANCEL_GENERATION: {
      // EXT-1973 dropped the sent snapshot here: a cancelled turn never made it
      // an upcoming baseline. The latch release goes through the shared helper
      // so it raises rather than assigns (a second release must not lower a
      // latch that is still owed).
      const released = releasePendingVccClears(state);
      if (
        released === state &&
        state.sentVccStyleOverrides === EMPTY_VCC_STYLE_OVERRIDES
      ) {
        // Nothing armed, nothing queued, no snapshot: identity, so subscribers
        // do not see a state change for a cancel that changed nothing.
        return state;
      }
      // Claim the apply's source snapshot so the turn end this cancel is about
      // to produce leaves it alone for the rollback to consume.
      const claimed =
        released.vccApplySourceSnapshot !== null
          ? {...released, vccApplySourceSnapshotClaimed: true}
          : released;
      // EXT-1973 dropped the sent snapshot on a cancel because a cancelled turn
      // never made it an upcoming baseline, and that still holds: nothing
      // reconciles against it now that the cancel restores the source instead.
      return {
        ...claimed,
        sentVccStyleOverrides: EMPTY_VCC_STYLE_OVERRIDES,
      };
    }
    case VCC_APPLY_SOURCE_SNAPSHOT_TAKEN: {
      // The *first* click's source wins while one is still live. EXT-2020 keeps
      // the apply record when a submit bails and folds it into the next click —
      // "the earlier bracket stays, so one Undo still pops both writes and the
      // card lists both batches" — so the two clicks are one apply as far as the
      // transcript is concerned. Overwriting here would leave the rollback
      // reverting only the second batch while the entry claimed both. A snapshot
      // is only live until the turn completes or the rollback consumes it, so a
      // click that starts a genuinely new apply still takes its own.
      if (state.vccApplySourceSnapshot !== null) return state;
      return {
        ...state,
        vccApplySourceSnapshot: {...action.payload, undoTurnId: null},
        vccApplySourceSnapshotClaimed: false,
      };
    }
    case VCC_APPLY_UNDO_TURN_ATTACHED: {
      const snapshot = state.vccApplySourceSnapshot;
      // Nothing live to attach to, or the first batch already named its bracket
      // — and that earlier one is what the folded transcript entry keys on.
      if (!snapshot || snapshot.undoTurnId !== null) return state;
      return {
        ...state,
        vccApplySourceSnapshot: {
          ...snapshot,
          undoTurnId: action.payload.turnId,
        },
      };
    }
    case VCC_APPLY_SOURCE_SNAPSHOT_DROPPED: {
      if (
        state.vccApplySourceSnapshot === null &&
        !state.vccApplySourceSnapshotClaimed
      ) {
        return state;
      }
      return {
        ...state,
        vccApplySourceSnapshot: null,
        vccApplySourceSnapshotClaimed: false,
      };
    }
    case VCC_APPLY_CANCELLED: {
      // EXT-1976: the user's own "stop this apply", which is not the same event
      // as the turn ending. It arrives with no turn in flight — the stuck bar in
      // the ticket is a clear left armed after the turn already ended — and it
      // does not arrive for a turn cancel with no user behind it.
      const released = releasePendingVccClears(state);
      return {
        ...released,
        sentVccStyleOverrides: EMPTY_VCC_STYLE_OVERRIDES,
        // Claim the source snapshot, so the turn end that follows a Stop does
        // not expire the rollback this cancel is asking for.
        ...(released.vccApplySourceSnapshot !== null
          ? {vccApplySourceSnapshotClaimed: true}
          : null),
        vccApplyCancelRequests: state.vccApplyCancelRequests + 1,
      };
    }
    case VCC_TEXT_OVERRIDES_UPDATED: {
      const {
        nodeId,
        nativeIdPath,
        tagName,
        original,
        modified,
        variant,
        componentState,
        sourceStamp,
        runScope,
      } = action.payload;
      const key = getTextOverrideKey({
        nodeId,
        variantId: variant?.id,
        runIndex: runScope?.index,
        scenarioId: componentState?.id,
      });

      // Drop the entry when modified equals original — no-op edits
      // shouldn't show up in the chat as pending changes.
      if (!vccTextOverrideHasChanges(original, modified)) {
        if (!(key in state.vccTextOverrides)) return state;
        const {[key]: _removed, ...rest} = state.vccTextOverrides;
        return {...state, vccTextOverrides: rest};
      }

      const previous = state.vccTextOverrides[key];

      return {
        ...state,
        hasMadeVisualEdit: true,
        vccTextOverrides: {
          ...state.vccTextOverrides,
          [key]: {
            nodeId,
            nativeIdPath,
            tagName,
            original,
            modified,
            variant,
            sourceStamp,
            runScope,
            ...(componentState ? {componentState} : {}),
            ...(previous?.hasConnectedProp !== undefined
              ? {hasConnectedProp: previous.hasConnectedProp}
              : {}),
            ...(previous?.propConnectionRevision
              ? {propConnectionRevision: previous.propConnectionRevision}
              : {}),
          },
        },
      };
    }
    case VCC_TEXT_OVERRIDES_CLEARED: {
      if (state.vccTextOverrides === EMPTY_VCC_TEXT_OVERRIDES) return state;
      return {...state, vccTextOverrides: EMPTY_VCC_TEXT_OVERRIDES};
    }
    case VCC_INSTANT_APPLY_ROLLED_BACK: {
      // EXT-1976: Cancel undoes the whole apply, so the queue has to come back
      // as it was — the caller saves the pre-apply source, this restores the
      // drafts that source was written from. Anything the user has re-edited
      // since keeps the newer draft: that is what the canvas is showing.
      const {style, text, styleQueuedAtApply, textQueuedAtApply} =
        action.payload;

      // Edits made *during* the apply are unqueued rather than kept. The
      // rollback restores `sourceBeforeApply`, so a draft authored against the
      // source the apply produced no longer describes anything that exists —
      // `planInstantTextApply` locates by `originalText`, and it would not
      // resolve. Cancel therefore returns the queue the user confirmed, not
      // that queue plus whatever they typed while it ran.
      //
      // The confirmed queue rides on the action, captured at the click. Reading
      // it from `sentVcc*Overrides` here would be wrong: a cancel clears that
      // snapshot, and on the counter-driven paths VCC_APPLY_CANCELLED lands
      // before this action, so the drafts still awaiting the assistant would be
      // absent from both sources and get deleted rather than kept.
      const keptStyleKeys = new Set([
        ...Object.keys(styleQueuedAtApply),
        ...Object.keys(style),
      ]);
      const keptTextKeys = new Set([
        ...Object.keys(textQueuedAtApply),
        ...Object.keys(text),
      ]);

      // Style drafts were marked applied, not removed, so un-marking is what
      // puts them back in front of the assistant and back in the bar's count.
      // Re-inserted as well when this apply's rebuild already landed and
      // CODE_EVALUATED pruned them, since the source that justified the prune
      // is the one being saved away.
      let nextStyle: VCCStyleOverridesMap = {};
      let styleChanged = false;
      for (const [key, entry] of Object.entries(state.vccStyleOverrides)) {
        if (keptStyleKeys.has(key)) nextStyle[key] = entry;
        else styleChanged = true;
      }
      for (const [key, entry] of Object.entries(style)) {
        if (key in nextStyle) continue;
        nextStyle[key] = entry;
        styleChanged = true;
      }
      if (!styleChanged) nextStyle = state.vccStyleOverrides;

      const nextApplied: VCCStyleOverridesMap = {};
      for (const [key, entry] of Object.entries(
        state.instantAppliedVccStyleOverrides
      )) {
        if (key in style) continue;
        nextApplied[key] = entry;
      }

      // Text drafts were deleted outright by VCC_TEXT_OVERRIDE_GROUP_REMOVED;
      // the entries come back from the payload because nothing else holds them.
      let nextText: VCCTextOverridesMap = {};
      let textChanged = false;
      for (const [key, entry] of Object.entries(state.vccTextOverrides)) {
        if (keptTextKeys.has(key)) nextText[key] = entry;
        else textChanged = true;
      }
      for (const [key, entry] of Object.entries(text)) {
        if (key in nextText) continue;
        nextText[key] = entry;
        textChanged = true;
      }
      if (!textChanged) nextText = state.vccTextOverrides;

      // Run the restored maps back through the release so the skip latches are
      // computed from what the drafts are *after* the rollback, not from
      // whatever they were when the cancel arrived. The toolbar restores before
      // dispatching VCC_APPLY_CANCELLED, but the counter-driven surfaces (the
      // assistant panel's Stop, the clarification Stop) cannot: their reducer
      // runs first and the restore lands in a later effect. Without this, a
      // cancel taken while the maps were momentarily empty leaves the latch
      // down, and the cancelled turn's segment end re-arms the clear that then
      // wipes the drafts this case just put back.
      return releasePendingVccClears({
        ...state,
        vccStyleOverrides: nextStyle,
        instantAppliedVccStyleOverrides:
          Object.keys(nextApplied).length === 0
            ? EMPTY_VCC_STYLE_OVERRIDES
            : nextApplied,
        vccTextOverrides: nextText,
        // Both described a rewrite that is being saved away: the chat's applied
        // card (EXT-1958) and the code editor's flash. Dropping the report to
        // null is the same edge that retires it when the user leaves the
        // component, so the card clears rather than sitting on a stale summary.
        vccInstantApplyHighlight: null,
        vccInstantApplyReport: null,
      });
    }
    case VCC_TEXT_OVERRIDE_GROUP_REMOVED: {
      const {nodeId, variantId, runIndex, scenarioId} = action.payload;
      const nextOverrides: VCCTextOverridesMap = {};
      let removedAny = false;
      for (const [key, entry] of Object.entries(state.vccTextOverrides)) {
        if (
          entry.nodeId === nodeId &&
          (entry.variant?.id ?? undefined) === variantId &&
          // A run-narrowed removal must leave the node's other run drafts
          // pending — they haven't been applied.
          (runIndex === undefined || entry.runScope?.index === runIndex) &&
          // Likewise a state-narrowed one leaves the other states' drafts.
          (scenarioId === undefined || entry.componentState?.id === scenarioId)
        ) {
          removedAny = true;
          continue;
        }
        nextOverrides[key] = entry;
      }
      if (!removedAny) return state;
      return {...state, vccTextOverrides: nextOverrides};
    }
    case VCC_TEXT_EDIT_STARTED: {
      const current = state.vccTextEditing;
      if (current && isSameVCCTextEditingTarget(current, action.payload)) {
        return state;
      }
      return {...state, vccTextEditing: action.payload};
    }
    case VCC_TEXT_EDIT_ENDED: {
      if (state.vccTextEditing === null) return state;
      return {...state, vccTextEditing: null};
    }
    case VCC_INSTANT_TEXT_APPLIED: {
      return {
        ...state,
        vccInstantApplyHighlight: {
          ...action.payload,
          version: (state.vccInstantApplyHighlight?.version ?? 0) + 1,
        },
      };
    }
    case VCC_INSTANT_APPLY_REPORTED: {
      return {
        ...state,
        vccInstantApplyReport: {
          summary: action.payload.summary,
          turnId: action.payload.turnId,
          version: (state.vccInstantApplyReport?.version ?? 0) + 1,
          // Reset, not carried over: this apply's rebuild has not started yet,
          // and inheriting the previous one's snapshot would let the build it
          // is still waiting on read as already done.
          buildStartVersion: null,
        },
      };
    }
    case VCC_SELECTED_ELEMENT_REVEALED: {
      const range = action.payload;
      if (range === null) {
        if (state.vccSelectedSourceRange === null) return state;
        return {...state, vccSelectedSourceRange: null};
      }
      // Same lines as last time → same reference, so the editor doesn't re-scroll.
      // Re-selecting the same element (or an unrelated re-dispatch) shouldn't yank
      // the viewport back.
      const current = state.vccSelectedSourceRange;
      if (
        current &&
        current.fromLine === range.fromLine &&
        current.toLine === range.toLine
      ) {
        return state;
      }
      return {
        ...state,
        vccSelectedSourceRange: {
          ...range,
          version: (current?.version ?? 0) + 1,
        },
      };
    }
    case VCC_STYLE_OVERRIDES_CLEARED: {
      // Preserve deferred-clear flags so the deferred-eval lock
      // (and its success toast) stay gated on CODE_EVALUATED, not on external
      // clears.
      if (state.vccStyleOverrides === EMPTY_VCC_STYLE_OVERRIDES) return state;
      return {
        ...state,
        vccStyleOverrides: EMPTY_VCC_STYLE_OVERRIDES,
        instantAppliedVccStyleOverrides: EMPTY_VCC_STYLE_OVERRIDES,
      };
    }
    case SYMBOL_NODE_UNFOCUSED:
    case COMPONENT_NODE_UNFOCUSED_ALL:
    case NAVIGATED_BACK_FROM_COMPONENT_EDIT: {
      const styleUnchanged =
        state.vccStyleOverrides === EMPTY_VCC_STYLE_OVERRIDES;
      const textUnchanged = state.vccTextOverrides === EMPTY_VCC_TEXT_OVERRIDES;
      const editingCleared = state.vccTextEditing === null;
      const activeFileCleared = state.activeSourceFileKey === null;
      if (
        styleUnchanged &&
        !state.clearVccStyleOverridesOnNextEval &&
        !state.skipVccStyleOverridesClearOnNextTurnEnd &&
        state.sentVccStyleOverrides === EMPTY_VCC_STYLE_OVERRIDES &&
        textUnchanged &&
        editingCleared &&
        state.vccInstantApplyHighlight === null &&
        state.vccInstantApplyReport === null &&
        state.vccSelectedSourceRange === null &&
        activeFileCleared
      ) {
        return state;
      }
      return {
        ...state,
        vccStyleOverrides: EMPTY_VCC_STYLE_OVERRIDES,
        clearVccStyleOverridesOnNextEval: false,
        skipVccStyleOverridesClearOnNextTurnEnd: false,
        sentVccStyleOverrides: EMPTY_VCC_STYLE_OVERRIDES,
        instantAppliedVccStyleOverrides: EMPTY_VCC_STYLE_OVERRIDES,
        vccTextOverrides: EMPTY_VCC_TEXT_OVERRIDES,
        vccTextEditing: null,
        // A highlight is only meaningful inside the component it was applied
        // in — drop it so it can't replay in the next editing session. Same for
        // the report: its drafts named elements of the component being left.
        vccInstantApplyHighlight: null,
        vccInstantApplyReport: null,
        vccSelectedSourceRange: null,
        // Leaving the component drops the active-file selection so the editor
        // doesn't show a stale file when a different component is focused.
        activeSourceFileKey: null,
        // Drafts stay, for the same reason as PAGE_SWITCHED above.
      };
    }
    case LOCAL_DEV_SERVER_REACHABILITY_CHANGED: {
      if (state.localDevServerReachable === action.payload.isReachable) {
        return state;
      }
      return {...state, localDevServerReachable: action.payload.isReachable};
    }
    case LOCAL_DEV_LIBRARY_DISCOVERED: {
      return {
        ...state,
        localDevDiscoveredDescriptor: action.payload.descriptor,
      };
    }
    case LOCAL_OVERRIDE_OPT_OUT_CHANGED: {
      const {profileId, isOptedOut} = action.payload;
      if (
        state.localOverrideOptedOutProfileIds.includes(profileId) === isOptedOut
      ) {
        return state;
      }
      return {
        ...state,
        localOverrideOptedOutProfileIds: isOptedOut
          ? [...state.localOverrideOptedOutProfileIds, profileId]
          : state.localOverrideOptedOutProfileIds.filter(
              (id) => id !== profileId
            ),
      };
    }
    default: {
      return state;
    }
  }
};
